import { Eye, Flame, Star, Target, UserCheck, Users, Video } from "lucide-react"
import type { LucideIcon } from "lucide-react"

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { UserBadge, UserBadgeBadgeType } from "@/types/serializers"

interface BadgeInfo {
  name: string
  description: string
  icon: string
}

interface Props {
  badges: UserBadge[]
  badgeDisplayInfo: Record<string, BadgeInfo>
  isOwnProfile: boolean
}

const ICON_MAP: Record<string, LucideIcon> = {
  "user-check": UserCheck,
  video: Video,
  users: Users,
  eye: Eye,
  target: Target,
  star: Star,
  flame: Flame,
}

export default function PlayerBadges({
  badges,
  badgeDisplayInfo,
  isOwnProfile,
}: Props) {
  const unlockedTypes = new Set(badges.map((b) => b.badge_type))
  const allBadgeTypes = Object.keys(badgeDisplayInfo)

  const badgesToShow = isOwnProfile
    ? allBadgeTypes
    : allBadgeTypes.filter((type) =>
        unlockedTypes.has(type as UserBadgeBadgeType),
      )

  if (badgesToShow.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-2">
      {badgesToShow.map((badgeType) => {
        const info = badgeDisplayInfo[badgeType]
        const isUnlocked = unlockedTypes.has(badgeType as UserBadgeBadgeType)
        const IconComponent = ICON_MAP[info.icon] ?? Star

        return (
          <Tooltip key={badgeType}>
            <TooltipTrigger asChild>
              <div
                data-badge={badgeType}
                data-badge-locked={!isUnlocked ? "true" : "false"}
                aria-label={info.name}
                title={`${info.name}: ${info.description}`}
                className={`flex h-8 w-8 cursor-default items-center justify-center rounded-full transition-colors ${
                  isUnlocked
                    ? "bg-krown-accent/20 text-krown-accent"
                    : "bg-muted/50 text-krown-grey-50"
                }`}
              >
                <IconComponent className="h-4 w-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-semibold">{info.name}</p>
              <p className="text-xs opacity-80">{info.description}</p>
            </TooltipContent>
          </Tooltip>
        )
      })}
    </div>
  )
}
